// ================================================================
// OAuth — نسخه واقعی، با polling (مقاوم در برابر COOP)
// ================================================================
// frontend (:5175) و backend (:3000) origin متفاوت دارن. به‌جای
// postMessage (که COOP بلاکش می‌کنه) از polling استفاده می‌کنیم:
// popup باز می‌شه، و /api/connections رو poll می‌کنیم تا connection
// که lastUsedAt ـش تازه شده پیدا بشه (هم برای جدید، هم برای reconnect).
import { apiRequest } from './apiClient.js';
import { t } from '../i18n/index.js';

const DEV_MODE = false;
const TOKEN_KEY = 'levelupwerk:token';

function getToken() {
  return localStorage.getItem(TOKEN_KEY);
}

const providerEnum = (id) =>
  id === 'google' ? 'GOOGLE'
  : id === 'google-calendar' ? 'GOOGLE_CALENDAR'
  : id === 'microsoft-calendar' ? 'MICROSOFT_CALENDAR'
  : id === 'microsoft' ? 'MICROSOFT'
  : id.toUpperCase();

const activeFlows = new Map();

// Repeated clicks share one popup/request loop, including across UI surfaces.
export function startOAuthFlow(providerId, startPath) {
  const key = `${providerId}:${startPath}`;
  if (activeFlows.has(key)) return activeFlows.get(key);
  const pending = runOAuthFlow(providerId, startPath).finally(() => activeFlows.delete(key));
  activeFlows.set(key, pending);
  return pending;
}

async function runOAuthFlow(providerId, startPath) {
  if (DEV_MODE) return await mockOAuthFlow(providerId);

  const token = getToken();
  if (!token) throw new Error(t('oauth.notLoggedIn'));

  // زمان شروع — هر connection که از این لحظه به بعد استفاده شده = نتیجه
  const startedAt = Date.now() - 2000;

  // Popup SOFORT und SYNCHRON öffnen — iOS Safari erlaubt window.open nur
  // direkt in der User-Geste; nach einem await ist der Gesten-Kontext weg
  // und der Popup wird geblockt («Popup blocked»). Darum: erst leeres
  // Fenster öffnen, DANN die Consent-URL laden und es dorthin navigieren.
  const w = 500;
  const h = 650;
  const left = window.screen.width / 2 - w / 2;
  const top = window.screen.height / 2 - h / 2;
  const popup = window.open(
    '',
    `oauth_${providerId}`,
    `width=${w},height=${h},left=${left},top=${top}`,
  );
  if (!popup) throw new Error(t('oauth.popupBlocked'));

  let url;
  let resultCode;
  try {
    ({ url, resultCode } = await apiRequest(startPath));
    if (!url) throw new Error(t('oauth.noAuthUrl'));
  } catch (e) {
    try { popup.close(); } catch { /* egal */ }
    throw e;
  }
  popup.location.href = url;

  // Allow time to review Google permissions; remain within the signed state TTL.
  const deadline = Date.now() + 9 * 60 * 1000;
  while (Date.now() < deadline) {
    await new Promise((r) => setTimeout(r, 1500));

    let list;
    try {
      if (resultCode && startPath.startsWith('/api/auth/google/')) {
        const response = await apiRequest('/api/auth/google/result/' + encodeURIComponent(resultCode));
        if (!response.result) continue;
        try { popup.close(); } catch { /* COOP */ }
        if (!response.result.success) throw Object.assign(new Error(t('errors.' + response.result.error)), { status: 400 });
        return { ...response.result, provider: providerId };
      }
      list = await apiRequest('/api/connections');
    } catch (error) {
      // Continuing after a throttle or lost permission only floods the API.
      // COOP can make popup.closed unreliable, so polling remains the fallback.
      if (error.status >= 400 && error.status < 500) {
        try { popup.close(); } catch { /* already closed */ }
        throw error;
      }
      continue; // خطای موقت شبکه → ادامه
    }

    // «frisch» = lastUsedAt ODER createdAt nach dem Start — beim ALLERERSTEN
    // Verbinden setzt der Callback nur createdAt; wer nur auf lastUsedAt
    // wartet, dreht 3 Minuten im «Loading…» (Mohammads iPhone-Hänger).
    const fresh = (list.connections || []).find(
      (c) =>
        c.provider === providerEnum(providerId) &&
        c.isValid !== false &&
        ((c.lastUsedAt && new Date(c.lastUsedAt).getTime() >= startedAt) ||
         (c.createdAt && new Date(c.createdAt).getTime() >= startedAt)),
    );

    if (fresh) {
      try { popup.close(); } catch {}
      return {
        connectionId: fresh.id,
        email: fresh.email,
        provider: providerId,
        connectedAt: fresh.createdAt || new Date().toISOString(),
      };
    }
  }

  try { popup.close(); } catch {}
  throw new Error(t('oauth.timeout'));
}

async function mockOAuthFlow(providerId) {
  const domain = providerId === 'google' ? 'gmail.com' : 'outlook.com';
  const email = window.prompt(`[DEV] ${providerId}\n\nE-Mail:`, `you@${domain}`);
  if (!email) return null;
  await new Promise((r) => setTimeout(r, 300));
  return {
    connectionId: 'mock_' + Date.now(),
    email,
    provider: providerId,
    connectedAt: new Date().toISOString(),
  };
}

// Manuelle IMAP/SMTP-Verbindung: prüft die Zugangsdaten serverseitig LIVE
// (imapflow + nodemailer) und speichert sie bei Erfolg als IMAP-Connection.
export async function connectImap(config) {
  return await apiRequest('/api/connections/imap', {
    method: 'POST',
    body: JSON.stringify(config),
  });
}
